'use client';

import { useEffect, useState } from 'react';

export default function Error({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    // 记录详细错误信息到控制台
    console.group('🔴 Application Error');
    console.error('Error:', error);
    console.error('Message:', error.message);
    console.error('Name:', error.name);
    console.error('Stack:', error.stack);
    if (error.digest) {
      console.error('Digest:', error.digest);
    }
    console.groupEnd();

    // 这里可以集成错误监控服务，如 Sentry
    // if (typeof window !== 'undefined') {
    //   Sentry.captureException(error);
    // }
  }, [error]);

  const copyErrorToClipboard = () => {
    const errorText = `
错误信息：
类型: ${error.name}
消息: ${error.message}
${error.digest ? `错误 ID: ${error.digest}` : ''}
时间: ${new Date().toISOString()}

堆栈跟踪:
${error.stack || '无堆栈信息'}

页面: ${window.location.href}
用户代理: ${navigator.userAgent}
    `.trim();

    navigator.clipboard.writeText(errorText).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-gray-50 px-4 py-8">
      <div className="w-full max-w-4xl">
        <div className="text-center mb-8">
          <div className="text-6xl mb-4">⚠️</div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            页面出错了
          </h1>
          <p className="text-gray-600 mb-6">
            抱歉，页面遇到了一个错误。下方显示了详细的错误信息，可以帮助定位问题。
          </p>
        </div>

        {/* 错误基本信息 */}
        <div className="bg-white rounded-lg shadow-lg p-6 mb-4">
          <div className="mb-4">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">错误信息</h2>
            <div className="space-y-3">
              <div>
                <span className="font-semibold text-gray-700">错误类型：</span>
                <span className="ml-2 text-red-600 font-mono">{error.name}</span>
              </div>
              <div>
                <span className="font-semibold text-gray-700">错误消息：</span>
                <div className="mt-1 p-3 bg-red-50 border border-red-200 rounded">
                  <p className="text-red-700 font-mono text-sm break-words">{error.message}</p>
                </div>
              </div>
              {error.digest && (
                <div>
                  <span className="font-semibold text-gray-700">错误 ID：</span>
                  <span className="ml-2 text-gray-600 font-mono text-sm">{error.digest}</span>
                </div>
              )}
              <div>
                <span className="font-semibold text-gray-700">发生时间：</span>
                <span className="ml-2 text-gray-600">{new Date().toLocaleString('zh-CN')}</span>
              </div>
            </div>
          </div>
        </div>

        {/* 堆栈跟踪 */}
        {error.stack && (
          <div className="bg-white rounded-lg shadow-lg p-6 mb-4">
            <details open={process.env.NODE_ENV === 'development'}>
              <summary className="cursor-pointer font-semibold text-gray-900 mb-3 text-lg hover:text-blue-600">
                📍 堆栈跟踪 (点击展开/收起)
              </summary>
              <div className="mt-3 bg-gray-900 text-gray-100 rounded-lg p-4 overflow-x-auto">
                <pre className="text-xs font-mono whitespace-pre-wrap break-words">
                  {error.stack}
                </pre>
              </div>
              <p className="mt-3 text-sm text-gray-600">
                💡 提示：堆栈跟踪显示了错误发生的具体位置和调用路径。
                查看第一行通常能找到错误发生的源文件和行号。
              </p>
            </details>
          </div>
        )}

        {/* 调试建议 */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mb-6">
          <h3 className="font-semibold text-blue-900 mb-3">🔍 调试建议</h3>
          <ul className="space-y-2 text-blue-800 text-sm">
            <li className="flex items-start">
              <span className="mr-2">1.</span>
              <span>查看上方的<strong>堆栈跟踪</strong>，找到错误发生的具体文件和行号</span>
            </li>
            <li className="flex items-start">
              <span className="mr-2">2.</span>
              <span>打开浏览器开发者工具（F12），查看 Console 标签页的详细错误信息</span>
            </li>
            <li className="flex items-start">
              <span className="mr-2">3.</span>
              <span>在 URL 后添加 <code className="bg-blue-100 px-1 rounded">?debug=true</code> 启用 VConsole 查看移动端调试信息</span>
            </li>
            <li className="flex items-start">
              <span className="mr-2">4.</span>
              <span>点击下方&ldquo;复制错误信息&rdquo;按钮，将完整错误信息发送给技术支持</span>
            </li>
          </ul>
        </div>

        {/* 操作按钮 */}
        <div className="flex flex-col sm:flex-row gap-3 justify-center items-center">
          <button
            onClick={reset}
            className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium shadow-md"
          >
            🔄 重新尝试
          </button>
          <button
            onClick={() => window.location.href = '/'}
            className="px-6 py-3 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors font-medium shadow-md"
          >
            🏠 返回首页
          </button>
          <button
            onClick={copyErrorToClipboard}
            className="px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-medium shadow-md"
          >
            {copied ? '✓ 已复制' : '📋 复制错误信息'}
          </button>
        </div>
      </div>
    </div>
  );
}
